import type { VatCompanyAddress } from '~~/types/vatValidation';
import { isRegistryPostalCodeOptional } from '~~/helpers/vatValidation/isRegistryPostalCodeOptional';
import { POSTAL_CODE_FIELD } from '~~/types/checkout/PostalCodeCartError';

export const VAT_AUTOFILL_ADDRESS_FIELDS: Array<keyof VatCompanyAddress> = [
  'company',
  'streetName',
  'additionalAddressInfo',
  POSTAL_CODE_FIELD,
  'city',
];

export const hasRegistryAddressToValidate = (address?: Record<string, unknown> | null): boolean => {

  return Boolean(address?.id) && VAT_AUTOFILL_ADDRESS_FIELDS.some(field => address?.[field]);

};

/**
 * On markets where registry might not carry a postal code we can not treat missing postal code as a failed validation
 * and we need to ignore it when comparing the addresses.
 */
export const isFieldUnanswered = (
  field: keyof VatCompanyAddress,
  validationAddress: VatCompanyAddress,
): boolean =>
  field === POSTAL_CODE_FIELD
  && !validationAddress[POSTAL_CODE_FIELD]
  && isRegistryPostalCodeOptional(validationAddress.country);

/**
 * Address keys are built only from the mandatory fields of the BzSt payload; the address form's
 * other, conditional fields are ignored.
 */
export const getJudgedAddressKey = (address?: Record<string, unknown> | null): string | null => {

  if (!address) {

    return null;

  }

  return VAT_AUTOFILL_ADDRESS_FIELDS
    .map((field) => String(address[field] ?? ''))
    .join('|');

};

/**
 * A registry that never discloses what it holds (BzSt) reports a mismatched field without a value
 * to suggest, so there the field being listed at all is the mismatch.
 */
export const isFieldMismatched = ({
  mismatches,
  field,
  isRegistryDataDisclosed,
}: {
  mismatches: Partial<Record<keyof VatCompanyAddress, string>>;
  field: keyof VatCompanyAddress;
  isRegistryDataDisclosed: boolean;
}): boolean => isRegistryDataDisclosed ? Boolean(mismatches[field]) : field in mismatches;

export const getAddressMismatch = (address: Record<string, unknown>, validationAddress: VatCompanyAddress) => {

  const mismatches: Partial<Record<keyof VatCompanyAddress, string>> = {};

  for (const field of VAT_AUTOFILL_ADDRESS_FIELDS) {

    if (isFieldUnanswered(field, validationAddress)) {

      continue;

    }

    if (String(address[field] ?? '') !== String(validationAddress[field] ?? '')) {

      mismatches[field] = String(validationAddress[field] ?? '');

    }

  }

  return mismatches;

};
